/**
 * Media Card Component
 * Displays a single ambient photo/video with delete and crop actions
 */

import { useState } from 'react';
import { Trash2, Loader2, Crop, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageCropDialog } from '@/components/ImageCropDialog';
import { OptimizationChip } from '@/components/OptimizationBadge';
import { updateAmbientPhotoCrop } from '@/services/ambientPhotoCropService';
import { toast } from 'sonner';
import type { AmbientMedia } from '@/services/ambientPhotoService';

function isVideo(contentType: string | undefined): boolean {
  return contentType?.startsWith('video/') ?? false;
}

export function MediaCard({ media, onDelete, onCropSuccess }: { media: AmbientMedia; onDelete: (id: string) => Promise<void>; onCropSuccess?: () => void }) {
  const [deleting, setDeleting] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [isProcessingCrop, setIsProcessingCrop] = useState(false);
  const video = isVideo(media.content_type);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await onDelete(media.id);
    } catch {
      setDeleting(false);
    }
  };

  const handleCropSave = async (
    cropData: { x: number; y: number; width: number; height: number },
    viewportPosition: { x: number; y: number }
  ) => {
    if (!media.attachment_id) {
      toast.error('No se puede procesar el crop: información incompleta');
      return;
    }
    setIsProcessingCrop(true);
    try {
      await updateAmbientPhotoCrop(media.id, media.attachment_id, cropData, viewportPosition);
      setCropDialogOpen(false);
      onCropSuccess?.();
      toast.success('Crop enviado para procesamiento');
    } catch (error) {
      console.error('Error al procesar crop:', error);
      toast.error('Error al procesar el crop');
    } finally {
      setIsProcessingCrop(false);
    }
  };

  return (
    <div className="relative rounded-lg border overflow-hidden group bg-gray-100 flex items-center justify-center aspect-[9/16]">
      {video ? (
        <div className="w-full h-full relative">
          <video
            src={media.url}
            className="w-full h-full object-cover"
            muted
            playsInline
          />
          <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded shadow-lg flex items-center gap-1">
            <Video className="h-3 w-3" />
            VIDEO
          </div>
        </div>
      ) : (
        <img
          src={media.url}
          alt={`Media de ambiente: ${media.filename}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
      {deleting && (
        <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-white animate-spin" aria-label="Eliminando" />
        </div>
      )}
      {!video && !deleting && (
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {media.optimization_status && (
            <OptimizationChip status={media.optimization_status} />
          )}
          <div className="flex gap-2">
            {media.attachment_id && (
              <button
                type="button"
                onClick={() => setCropDialogOpen(true)}
                disabled={isProcessingCrop}
                className="bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                title="Optimizar"
                aria-label="Optimizar"
              >
                <Crop className="h-4 w-4" />
              </button>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              className="h-9 w-9 p-0 rounded-full"
              aria-label={`Eliminar ${video ? 'video' : 'foto'}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      {video && !deleting && (
        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            className="h-9 w-9 p-0 rounded-full"
            aria-label={`Eliminar video`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {media.filename}
      </div>
      {!video && (
        <ImageCropDialog
          open={cropDialogOpen}
          onOpenChange={setCropDialogOpen}
          imageUrl={media.original_url || media.url}
          onSave={handleCropSave}
          isProcessing={isProcessingCrop}
          initialCrop={media.crop_data || null}
          initialViewport={
            media.viewport_position?.x !== undefined && media.viewport_position?.y !== undefined
              ? media.viewport_position
              : null
          }
        />
      )}
    </div>
  );
}
